import { AnimatePresence, motion } from 'framer-motion';
import { ArrowDown, ArrowUp, CalendarDays, CircleAlert, CircleCheck, CircleDashed, Circle, ExternalLink, ListChecks, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useSpatialNav } from '../hooks/useSpatialNav';
import { isOverdue, labelColor, localDay, textOn } from '../lib/planner';
import { ApiError, endpoints, type PlannerCard, type PlannerTaskDetails } from '../services/api';
import { usePlannerTask } from '../store/plannerTask';
import { Avatar } from './widgets/Planner';

const PROGRESS = {
  notStarted: { name: 'Not started', icon: Circle, className: 'opacity-70' },
  inProgress: { name: 'In progress', icon: CircleDashed, className: 'text-sky-400' },
  completed: { name: 'Completed', icon: CircleCheck, className: 'text-[var(--accent)]' },
} as const;

const PRIORITY: Record<PlannerCard['priority'], { name: string; icon: typeof ArrowUp; className: string } | null> = {
  urgent: { name: 'Urgent', icon: CircleAlert, className: 'text-red-400' },
  important: { name: 'Important', icon: ArrowUp, className: 'text-amber-300' },
  medium: null,
  low: { name: 'Low', icon: ArrowDown, className: 'opacity-70' },
};

const fmtDate = new Intl.DateTimeFormat(undefined, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });

/**
 * A Planner task's details – opened by clicking a task (or OK on the remote) in the Planner
 * widget: progress, priority, dates, labels, who it's assigned to, and – fetched when opened –
 * its description, checklist and links. Read-only; Back or Esc closes it.
 */
export function PlannerTaskModal() {
  const { open, close } = usePlannerTask();
  const dialog = useRef<HTMLDivElement>(null);
  const [details, setDetails] = useState<PlannerTaskDetails | null>(null);
  const [error, setError] = useState<string | null>(null);
  /** Planner is busy (Microsoft is limiting requests): tries again by itself. */
  const [busy, setBusy] = useState(false);
  useSpatialNav(dialog, open !== null);

  useEffect(() => {
    if (!open) return;
    setDetails(null);
    setError(null);
    setBusy(false);
    let cancelled = false;
    let retry: number | undefined;
    // Microsoft may be limiting Planner requests: try again every 20 s, up to 6 times.
    const load = (attempt: number) =>
      endpoints
        .plannerTaskDetails(open.source.planId, open.task.id, open.source.account, open.source.canvas)
        .then((r) => {
          if (cancelled) return;
          setBusy(false);
          setDetails(r.details);
        })
        .catch((e: unknown) => {
          if (cancelled) return;
          if (e instanceof ApiError && e.code === 'rate_limited' && attempt < 6) {
            setBusy(true);
            retry = window.setTimeout(() => void load(attempt + 1), 20_000);
            return;
          }
          setBusy(false);
          setError(
            e instanceof ApiError && e.code === 'rate_limited'
              ? 'Planner is busy right now – close this and try again in a few minutes.'
              : e instanceof Error
                ? e.message
                : 'Couldn’t load the details.',
          );
        });
    void load(0);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.key === 'GoBack' || e.key === 'BrowserBack') close();
    };
    window.addEventListener('keydown', onKey);
    const t = window.setTimeout(() => dialog.current?.querySelector<HTMLElement>('button')?.focus(), 50);
    return () => {
      cancelled = true;
      window.clearTimeout(retry);
      window.clearTimeout(t);
      window.removeEventListener('keydown', onKey);
    };
  }, [open, close]);

  const task = open?.task;
  const board = open?.board;
  const bucket = task && board?.buckets.find((b) => b.id === task.bucketId)?.name;
  const progress = task ? PROGRESS[task.progress] : null;
  const priority = task ? PRIORITY[task.priority] : null;
  const late = task ? isOverdue(task) : false;
  const unnamed = task && board ? task.assignees.some((id) => id !== board.me && !board.people[id]) : false;

  return (
    <AnimatePresence>
      {open && task && board && progress && (
        <motion.div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={close}
        >
          <motion.div
            ref={dialog}
            role="dialog"
            aria-modal="true"
            aria-label={`Task: ${task.title}`}
            className="relative max-h-full w-full max-w-xl overflow-y-auto rounded-2xl bg-[var(--surface-strong)] p-6 text-[var(--fg)] shadow-2xl"
            initial={{ y: 24, scale: 0.98 }}
            animate={{ y: 0, scale: 1 }}
            exit={{ y: 24, scale: 0.98 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <h2 className={`font-heading text-xl font-semibold leading-snug ${task.progress === 'completed' ? 'line-through opacity-70' : ''}`}>{task.title}</h2>
                <p className="muted mt-0.5 text-sm">
                  {board.plan.title}
                  {bucket ? ` · ${bucket}` : ''}
                </p>
              </div>
              <button onClick={close} className="shrink-0 rounded-full p-2 hover:bg-[var(--border)]" aria-label="Close">
                <X className="h-5 w-5" aria-hidden />
              </button>
            </div>

            <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
              <span className="flex items-center gap-1.5">
                <progress.icon className={`h-4 w-4 ${progress.className}`} aria-hidden /> {progress.name}
              </span>
              {priority && (
                <span className="flex items-center gap-1.5">
                  <priority.icon className={`h-4 w-4 ${priority.className}`} aria-hidden /> {priority.name}
                </span>
              )}
              {(task.start || task.due) && (
                <span className={`flex items-center gap-1.5 ${late ? 'font-medium text-red-400' : ''}`}>
                  <CalendarDays className="h-4 w-4" aria-hidden />
                  {task.start ? fmtDate.format(localDay(task.start)) : ''}
                  {task.start && task.due ? ' → ' : ''}
                  {task.due ? `${task.start ? '' : 'Due '}${fmtDate.format(localDay(task.due))}` : ''}
                  {late ? ' (late)' : ''}
                </span>
              )}
              {task.checklist && (
                <span className="flex items-center gap-1.5">
                  <ListChecks className="h-4 w-4" aria-hidden /> {task.checklist.done}/{task.checklist.total}
                </span>
              )}
            </div>

            {task.labels.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-1.5">
                {task.labels.map((l) => {
                  const c = labelColor(l);
                  return (
                    <span key={l} className="rounded px-2 py-0.5 text-xs font-medium" style={{ background: c, color: textOn(c) }}>
                      {board.labels[l] ?? 'Label'}
                    </span>
                  );
                })}
              </div>
            )}

            {task.assignees.length > 0 && (
              <section className="mt-5">
                <h3 className="muted mb-2 text-xs font-semibold uppercase tracking-wider">Assigned to</h3>
                <ul className="space-y-2">
                  {task.assignees.map((id) => {
                    const name = board.people[id];
                    return (
                      <li key={id} className="flex items-center gap-2.5 text-sm">
                        <Avatar id={id} board={board} source={open.source} size="h-8 w-8 text-xs" />
                        {id === board.me ? `You${name ? ` (${name})` : ''}` : (name ?? 'Name not available')}
                      </li>
                    );
                  })}
                </ul>
                {unnamed && !open.source.canvas && (
                  <p className="muted mt-2 text-xs">Names and photos of colleagues need permission: Settings → Widgets → Planner → Allow names.</p>
                )}
              </section>
            )}

            <section className="mt-5">
              <h3 className="muted mb-2 text-xs font-semibold uppercase tracking-wider">Notes</h3>
              {!details && !error && (
                <p className="muted text-sm">{busy ? 'Microsoft is limiting Planner requests – trying again shortly…' : 'Loading…'}</p>
              )}
              {error && <p className="text-sm text-red-400">{error}</p>}
              {details && (details.description ? <p className="whitespace-pre-wrap text-sm leading-relaxed">{details.description}</p> : <p className="muted text-sm">No notes.</p>)}
            </section>

            {details && details.checklist.length > 0 && (
              <section className="mt-5">
                <h3 className="muted mb-2 text-xs font-semibold uppercase tracking-wider">
                  Checklist · {details.checklist.filter((c) => c.done).length}/{details.checklist.length}
                </h3>
                <ul className="space-y-1.5">
                  {details.checklist.map((c, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm">
                      {c.done ? <CircleCheck className="mt-0.5 h-4 w-4 shrink-0 text-[var(--accent)]" aria-label="Done" /> : <Circle className="mt-0.5 h-4 w-4 shrink-0 opacity-60" aria-label="Not done" />}
                      <span className={c.done ? 'muted line-through' : ''}>{c.title}</span>
                    </li>
                  ))}
                </ul>
              </section>
            )}

            {details && details.links.length > 0 && (
              <section className="mt-5">
                <h3 className="muted mb-2 text-xs font-semibold uppercase tracking-wider">Attachments and links</h3>
                <ul className="space-y-1.5">
                  {details.links.map((l) => (
                    <li key={l.url} className="text-sm">
                      <a href={l.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-[var(--accent)] hover:underline">
                        <ExternalLink className="h-3.5 w-3.5" aria-hidden /> {l.name}
                      </a>
                    </li>
                  ))}
                </ul>
              </section>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
